import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PersistedColumnConfig } from '@/types/table/columnConfig';
import { isRecord } from '@/utils/filter/isRecord';

const STORAGE_KEY_PREFIX = 'table-config';

const buildStorageKey = (username: string, tableId: string): string => `${STORAGE_KEY_PREFIX}:${username}:${tableId}`;

const buildVisibilityKey = (visibility: PersistedColumnConfig['visibility']): string =>
  Object.keys(visibility)
    .sort()
    .map(columnId => `${columnId}:${visibility[columnId] ? '1' : '0'}`)
    .join('|');

const buildPinningKey = (pinning: PersistedColumnConfig['pinning']): string =>
  pinning
    .map(({ columnId, parentLabel = '', childLabel = '' }) => `${columnId}:${parentLabel}:${childLabel}`)
    .join('|');

const buildDefaultConfigKey = (defaultConfig: PersistedColumnConfig): string =>
  `${buildVisibilityKey(defaultConfig.visibility)}__${buildPinningKey(defaultConfig.pinning)}`;

const parseVisibility = (value: unknown): Record<string, boolean> | null => {
  if (!isRecord(value)) return null;

  const visibility: Record<string, boolean> = {};
  for (const [columnId, isVisible] of Object.entries(value)) {
    if (typeof isVisible !== 'boolean') return null;
    visibility[columnId] = isVisible;
  }

  return visibility;
};

const parsePinning = (value: unknown): PersistedColumnConfig['pinning'] | null => {
  if (!Array.isArray(value)) return null;

  return value.reduce<PersistedColumnConfig['pinning'] | null>((pinning, pinEntry) => {
    if (!pinning || !isRecord(pinEntry) || typeof pinEntry.columnId !== 'string') return null;

    const parsedPinEntry: PersistedColumnConfig['pinning'][number] = { columnId: pinEntry.columnId };
    if (typeof pinEntry.parentLabel === 'string') parsedPinEntry.parentLabel = pinEntry.parentLabel;
    if (typeof pinEntry.childLabel === 'string') parsedPinEntry.childLabel = pinEntry.childLabel;

    return [...pinning, parsedPinEntry];
  }, []);
};

const parsePersistedConfig = (value: unknown): PersistedColumnConfig | null => {
  if (!isRecord(value)) return null;

  const visibility = parseVisibility(value.visibility);
  const pinning = parsePinning(value.pinning);

  return visibility && pinning ? { visibility, pinning } : null;
};

const getSessionConfig = (storageKey: string): PersistedColumnConfig | null => {
  const storedValue = sessionStorage.getItem(storageKey);
  if (!storedValue) return null;

  try {
    return parsePersistedConfig(JSON.parse(storedValue));
  } catch {
    return null;
  }
};

interface UseTableConfigPersistenceParams {
  username: string | undefined;
  tableId: string;
  columnIds: string[];
  defaultConfig: PersistedColumnConfig;
}

interface UseTableConfigPersistenceResult {
  config: PersistedColumnConfig | null;
  isLoading: boolean;
  saveConfig: (newConfig: PersistedColumnConfig) => Promise<void>;
  resetConfig: () => Promise<void>;
}

export const useTableConfigPersistence = ({
  username,
  tableId,
  columnIds,
  defaultConfig,
}: UseTableConfigPersistenceParams): UseTableConfigPersistenceResult => {
  const [config, setConfig] = useState<PersistedColumnConfig | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(username && tableId));
  const columnIdsKey = useMemo(() => columnIds.join(','), [columnIds]);
  const defaultConfigKey = buildDefaultConfigKey(defaultConfig);
  const stableColumnIdsRef = useRef({ key: columnIdsKey, value: columnIds });
  const stableDefaultConfigRef = useRef({ key: defaultConfigKey, value: defaultConfig });

  if (stableColumnIdsRef.current.key !== columnIdsKey) {
    stableColumnIdsRef.current = { key: columnIdsKey, value: columnIds };
  }

  if (stableDefaultConfigRef.current.key !== defaultConfigKey) {
    stableDefaultConfigRef.current = { key: defaultConfigKey, value: defaultConfig };
  }

  const stableColumnIds = stableColumnIdsRef.current.value;
  const stableDefaultConfig = stableDefaultConfigRef.current.value;

  useEffect(() => {
    if (!username || !tableId) {
      setConfig(stableDefaultConfig);
      setIsLoading(false);
      return;
    }

    const storageKey = buildStorageKey(username, tableId);
    let isCancelled = false;

    setIsLoading(true);

    const persistedConfig = getSessionConfig(storageKey);
    if (isCancelled) return;

    const availableColumnIdSet = new Set(stableColumnIds);

    setConfig(
      persistedConfig
        ? {
            visibility: Object.fromEntries(
              stableColumnIds.map(columnId => [
                columnId,
                persistedConfig.visibility[columnId] ?? stableDefaultConfig.visibility[columnId] ?? true,
              ]),
            ),
            pinning: persistedConfig.pinning.filter(pinEntry => availableColumnIdSet.has(pinEntry.columnId)),
          }
        : stableDefaultConfig,
    );
    setIsLoading(false);

    return () => {
      isCancelled = true;
    };
  }, [stableDefaultConfig, username, tableId, stableColumnIds]);

  const saveConfig = useCallback(
    async (newConfig: PersistedColumnConfig) => {
      setConfig(newConfig);

      if (!username || !tableId) {
        return;
      }

      const storageKey = buildStorageKey(username, tableId);

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(newConfig));
      } catch {
        return;
      }
    },
    [username, tableId],
  );

  const resetConfig = useCallback(async () => {
    setConfig(stableDefaultConfig);

    if (!username || !tableId) {
      return;
    }

    const storageKey = buildStorageKey(username, tableId);

    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      return;
    }
  }, [stableDefaultConfig, username, tableId]);

  return { config, isLoading, saveConfig, resetConfig };
};
